import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/authContext";
import SEO from "@/components/seo/SEO";
import ChangePasswordModal from "@/components/admin/ChangePasswordModal";
import AdvertisingInquiries from "@/components/admin/AdvertisingInquiries";
import { toast } from "sonner";
import {
  LogOut, Plus, Search, Edit3, Trash2, Star, TrendingUp, Sparkles,
  Upload, X, Save, KeyRound, Filter, Eye, EyeOff, RotateCcw, Megaphone,
} from "lucide-react";

const TYPE_OPTIONS = [
  "movie", "series", "original", "documentary", "podcast", "comedy",
  "lifestyle", "live", "kids",
];

// Convert a stored poster URL (either "/covers/x.jpg" or "/api/files/ycn/covers/x.jpg") to a browser-loadable URL
function resolveImgSrc(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${url}`;
  return url;
}

function emptyTitle() {
  return {
    title: "", type: "movie", category: "", description: "",
    year: null, rating: "", duration: "", runtime_minutes: null,
    seasons: null, episodes: null, genres: [], poster_url: "", backdrop_url: "",
    trailer_url: "", video_url: "", is_original: false, is_trending: false,
    featured: false, published: true, cast: [], creator: "",
  };
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [titles, setTitles] = useState([]);
  const [busy, setBusy] = useState(true);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState(null);   // full title object being edited, or "new" object
  const [showPwd, setShowPwd] = useState(false);
  const [tab, setTab] = useState("catalog"); // catalog | advertising

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/titles", { params: { include_deleted: true } });
      setTitles(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { if (user && user.role === "admin") refresh(); }, [user, refresh]);

  // Force password modal on first login
  useEffect(() => {
    if (user?.needs_password_change) setShowPwd(true);
  }, [user]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return titles
      .filter((t) => showDeleted ? true : !t.is_deleted)
      .filter((t) => typeFilter === "all" ? true : t.type === typeFilter)
      .filter((t) => featuredOnly ? t.featured : true)
      .filter((t) => q ? (t.title.toLowerCase().includes(q) || t.slug.toLowerCase().includes(q)) : true);
  }, [titles, query, typeFilter, featuredOnly, showDeleted]);

  const counts = useMemo(() => {
    const active = titles.filter((t) => !t.is_deleted);
    return {
      total: active.length,
      featured: active.filter((t) => t.featured).length,
      trending: active.filter((t) => t.is_trending).length,
      original: active.filter((t) => t.is_original).length,
      deleted: titles.filter((t) => t.is_deleted).length,
    };
  }, [titles]);

  const quickToggle = async (title, field) => {
    try {
      const { data } = await api.patch(`/admin/titles/${title.id}`, { [field]: !title[field] });
      setTitles((arr) => arr.map((t) => t.id === data.id ? data : t));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const softDelete = async (title) => {
    if (!window.confirm(`Delete "${title.title}"? It will be hidden from the site but can be restored.`)) return;
    try {
      await api.delete(`/admin/titles/${title.id}`);
      setTitles((arr) => arr.map((t) => t.id === title.id ? { ...t, is_deleted: true } : t));
      toast.success(`"${title.title}" soft-deleted`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (title) => {
    try {
      const { data } = await api.post(`/admin/titles/${title.id}/restore`);
      setTitles((arr) => arr.map((t) => t.id === title.id ? data : t));
      toast.success(`"${title.title}" restored`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onSave = async (payload, isNew) => {
    try {
      if (isNew) {
        const { data } = await api.post("/admin/titles", payload);
        setTitles((arr) => [data, ...arr]);
        toast.success(`Created "${data.title}"`);
      } else {
        const { data } = await api.patch(`/admin/titles/${payload.id}`, payload);
        setTitles((arr) => arr.map((t) => t.id === data.id ? data : t));
        toast.success(`Saved "${data.title}"`);
      }
      setEditing(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <div className="pt-32 text-center text-white/50">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-page" className="min-h-screen pt-24 md:pt-28 pb-20 px-6 md:px-10">
      <SEO title="Admin" description="YCN admin — manage catalog." path="/admin" noindex />

      {/* Header */}
      <div className="max-w-[1500px] mx-auto flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-white/60 mb-1">Admin Console</div>
          <h1 className="font-display text-4xl md:text-5xl font-black">
            YCN <span className="ycn-gradient-text">Catalog</span>
          </h1>
          <div className="mt-2 text-sm text-white/60">
            Signed in as <span className="text-white/90">{user.email}</span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {tab === "catalog" && (
            <button data-testid="admin-open-create" onClick={() => setEditing({ ...emptyTitle(), _isNew: true })}
              className="ycn-btn-primary inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-white text-sm transition-all">
              <Plus size={16} /> New Title
            </button>
          )}
          <button data-testid="admin-open-password" onClick={() => setShowPwd(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full ycn-glass hover:bg-white/15 text-sm transition">
            <KeyRound size={14} /> Change Password
          </button>
          <button data-testid="admin-logout" onClick={() => { logout(); nav("/admin/login", { replace: true }); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/20 hover:bg-white/10 text-sm transition">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-[1500px] mx-auto flex items-center gap-2 mb-6 border-b border-white/10">
        <button
          data-testid="admin-tab-catalog"
          onClick={() => setTab("catalog")}
          className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition border-b-2 -mb-px ${tab === "catalog" ? "border-white text-white" : "border-transparent text-white/60 hover:text-white/85"}`}
        >
          <Sparkles size={14} /> Catalog
        </button>
        <button
          data-testid="admin-tab-advertising"
          onClick={() => setTab("advertising")}
          className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition border-b-2 -mb-px ${tab === "advertising" ? "border-white text-white" : "border-transparent text-white/60 hover:text-white/85"}`}
        >
          <Megaphone size={14} /> Advertising Inquiries
        </button>
      </div>

      {tab === "catalog" && (<>
      {/* Stat cards */}
      <div className="max-w-[1500px] mx-auto grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {[
          { l: "Total live", v: counts.total },
          { l: "Featured", v: counts.featured, icon: Star },
          { l: "Trending", v: counts.trending, icon: TrendingUp },
          { l: "YCN Originals", v: counts.original, icon: Sparkles },
          { l: "Soft-deleted", v: counts.deleted, icon: Trash2 },
        ].map(({ l, v, icon: Icon }) => (
          <div key={l} className="ycn-glass rounded-2xl p-4">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 flex items-center gap-1.5">
              {Icon && <Icon size={11} />} {l}
            </div>
            <div className="font-display text-2xl md:text-3xl font-black mt-1 tabular-nums">{v}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="max-w-[1500px] mx-auto flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-search"
            placeholder="Search title or slug…"
            className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-white/40" />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="admin-type-filter"
          className="rounded-full bg-white/5 border border-white/15 focus:border-white/40 outline-none px-4 py-2.5 text-sm text-white">
          <option value="all" className="bg-[#0f0f11]">All types</option>
          {TYPE_OPTIONS.map((t) => <option key={t} value={t} className="bg-[#0f0f11]">{t}</option>)}
        </select>
        <button onClick={() => setFeaturedOnly((v) => !v)} data-testid="admin-featured-filter"
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border text-sm transition ${featuredOnly ? "bg-white text-black border-white" : "bg-white/5 border-white/15 hover:bg-white/10 text-white"}`}>
          <Star size={13} /> Featured only
        </button>
        <button onClick={() => setShowDeleted((v) => !v)} data-testid="admin-deleted-filter"
          className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border text-sm transition ${showDeleted ? "bg-white text-black border-white" : "bg-white/5 border-white/15 hover:bg-white/10 text-white"}`}>
          <Filter size={13} /> {showDeleted ? "Hide deleted" : "Show deleted"}
        </button>
      </div>

      {/* Table */}
      <div className="max-w-[1500px] mx-auto ycn-glass rounded-2xl overflow-hidden border border-white/10">
        <div className="grid grid-cols-[80px,1fr,110px,90px,90px,90px,140px] items-center px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-white/50 border-b border-white/10 bg-white/5">
          <div>Art</div><div>Title</div><div>Type</div>
          <div className="text-center">Featured</div>
          <div className="text-center">Trending</div>
          <div className="text-center">Original</div>
          <div className="text-right">Actions</div>
        </div>
        {busy && titles.length === 0 && <div className="p-8 text-center text-white/50">Loading catalog…</div>}
        {!busy && filtered.length === 0 && <div className="p-8 text-center text-white/50">No titles match.</div>}
        <div className="max-h-[62vh] overflow-y-auto">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`admin-row-${t.slug}`}
              className={`grid grid-cols-[80px,1fr,110px,90px,90px,90px,140px] items-center px-4 py-2.5 border-b border-white/5 hover:bg-white/[0.03] ${t.is_deleted ? "opacity-50" : ""}`}>
              <div className="h-14 w-10 rounded-md overflow-hidden bg-white/5 border border-white/10">
                {t.poster_url
                  ? <img src={resolveImgSrc(t.poster_url)} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gradient-to-br from-violet-700 via-blue-800 to-orange-600" />}
              </div>
              <div className="min-w-0">
                <div className="font-semibold truncate flex items-center gap-1.5">
                  {t.title}
                  {t.is_deleted && <span className="text-[9px] uppercase tracking-widest text-red-300 border border-red-400/30 rounded px-1.5 py-0.5">deleted</span>}
                </div>
                <div className="text-[11px] text-white/45 truncate">{t.slug} {t.category ? `· ${t.category}` : ""}</div>
              </div>
              <div className="text-xs uppercase tracking-widest text-white/60">{t.type}</div>
              <ToggleBtn active={t.featured} onClick={() => quickToggle(t, "featured")} disabled={t.is_deleted} testId={`toggle-featured-${t.slug}`} />
              <ToggleBtn active={t.is_trending} onClick={() => quickToggle(t, "is_trending")} disabled={t.is_deleted} testId={`toggle-trending-${t.slug}`} />
              <ToggleBtn active={t.is_original} onClick={() => quickToggle(t, "is_original")} disabled={t.is_deleted} testId={`toggle-original-${t.slug}`} />
              <div className="flex justify-end gap-1.5">
                {t.is_deleted ? (
                  <button data-testid={`restore-${t.slug}`} onClick={() => restore(t)} title="Restore"
                    className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-emerald-300">
                    <RotateCcw size={15} />
                  </button>
                ) : (
                  <>
                    <button data-testid={`edit-${t.slug}`} onClick={() => setEditing({ ...t })} title="Edit"
                      className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/80">
                      <Edit3 size={15} />
                    </button>
                    <button data-testid={`delete-${t.slug}`} onClick={() => softDelete(t)} title="Delete"
                      className="h-9 w-9 rounded-lg hover:bg-red-500/15 flex items-center justify-center text-red-300">
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && <EditTitleDrawer title={editing} onClose={() => setEditing(null)} onSave={onSave} />}
      </>)}

      {tab === "advertising" && (
        <AdvertisingInquiries />
      )}

      <ChangePasswordModal open={showPwd} forced={!!user?.needs_password_change} onClose={() => setShowPwd(false)} />
    </div>
  );
}

function ToggleBtn({ active, onClick, disabled, testId }) {
  return (
    <div className="flex justify-center">
      <button data-testid={testId} onClick={onClick} disabled={disabled}
        aria-pressed={active}
        className={`relative w-10 h-6 rounded-full transition disabled:opacity-40 ${active ? "bg-gradient-to-r from-violet-500 via-blue-500 to-orange-500" : "bg-white/10"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${active ? "left-4" : "left-0.5"}`} />
      </button>
    </div>
  );
}

// ---------------- Edit/Create Drawer ----------------
function EditTitleDrawer({ title, onClose, onSave }) {
  const isNew = !!title._isNew;
  const [form, setForm] = useState(() => ({ ...title }));
  const [uploading, setUploading] = useState({ poster: false, backdrop: false });
  const posterRef = useRef(null);
  const backdropRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(kind === "poster" ? "poster_url" : "backdrop_url", data.url);
      toast.success(`${kind} uploaded`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading((u) => ({ ...u, [kind]: false })); }
  };

  const submit = async (e) => {
    e.preventDefault();
    // Strip fields we don't send back (id/slug/created_at/needs_editorial/is_deleted)
    const { _isNew, _id, needs_editorial, is_deleted, created_at, ...payload } = form;
    // Coerce numeric fields
    ["year", "runtime_minutes", "seasons", "episodes"].forEach((k) => {
      if (payload[k] === "" || payload[k] === null || Number.isNaN(Number(payload[k]))) payload[k] = null;
      else payload[k] = Number(payload[k]);
    });
    // Coerce comma-separated arrays
    ["genres", "cast"].forEach((k) => {
      if (typeof payload[k] === "string") payload[k] = payload[k].split(",").map((s) => s.trim()).filter(Boolean);
    });
    if (isNew) {
      delete payload.id;
      delete payload.slug;
    }
    await onSave(payload, isNew);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl h-full overflow-y-auto bg-[#0a0a0c] border-l border-white/10 shadow-2xl" data-testid="edit-drawer">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-[#0a0a0c]/95 backdrop-blur border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">{isNew ? "Create new title" : "Editing"}</div>
            <div className="font-display text-xl font-bold">{form.title || "New Title"}</div>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="h-9 w-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-6">
          {/* Artwork uploads */}
          <div className="grid grid-cols-2 gap-4">
            <ArtUpload label="Poster (2:3)" aspect="aspect-[2/3]"
              value={form.poster_url} onChange={(v) => set("poster_url", v)}
              onFile={(f) => upload("poster", f)} uploading={uploading.poster}
              refInput={posterRef} testId="art-poster" />
            <ArtUpload label="Backdrop (16:9)" aspect="aspect-video"
              value={form.backdrop_url} onChange={(v) => set("backdrop_url", v)}
              onFile={(f) => upload("backdrop", f)} uploading={uploading.backdrop}
              refInput={backdropRef} testId="art-backdrop" />
          </div>

          {/* Basics */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Title" required>
              <input required value={form.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="field-title"
                className="ycn-input" />
            </Field>
            <Field label="Type">
              <select value={form.type || "movie"} onChange={(e) => set("type", e.target.value)} data-testid="field-type" className="ycn-input">
                {TYPE_OPTIONS.map((t) => <option key={t} value={t} className="bg-[#0f0f11]">{t}</option>)}
              </select>
            </Field>
            <Field label="Category">
              <input value={form.category || ""} onChange={(e) => set("category", e.target.value)} data-testid="field-category" className="ycn-input" placeholder="Drama, Suspense, Comedy…" />
            </Field>
            <Field label="Genres (comma-separated)">
              <input value={Array.isArray(form.genres) ? form.genres.join(", ") : form.genres || ""} onChange={(e) => set("genres", e.target.value)} data-testid="field-genres" className="ycn-input" />
            </Field>
            <Field label="Year">
              <input type="number" value={form.year ?? ""} onChange={(e) => set("year", e.target.value)} data-testid="field-year" className="ycn-input" />
            </Field>
            <Field label="Rating">
              <input value={form.rating || ""} onChange={(e) => set("rating", e.target.value)} data-testid="field-rating" className="ycn-input" placeholder="PG-13, TV-MA…" />
            </Field>
            <Field label="Runtime (min)">
              <input type="number" value={form.runtime_minutes ?? ""} onChange={(e) => set("runtime_minutes", e.target.value)} data-testid="field-runtime" className="ycn-input" />
            </Field>
            <Field label="Duration text">
              <input value={form.duration || ""} onChange={(e) => set("duration", e.target.value)} data-testid="field-duration" className="ycn-input" placeholder='e.g. "1h 42m"' />
            </Field>
            <Field label="Seasons">
              <input type="number" value={form.seasons ?? ""} onChange={(e) => set("seasons", e.target.value)} data-testid="field-seasons" className="ycn-input" />
            </Field>
            <Field label="Episodes">
              <input type="number" value={form.episodes ?? ""} onChange={(e) => set("episodes", e.target.value)} data-testid="field-episodes" className="ycn-input" />
            </Field>
          </div>

          <Field label="Description">
            <textarea rows={4} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="field-description" className="ycn-input !rounded-2xl" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Cast (comma-separated)">
              <input value={Array.isArray(form.cast) ? form.cast.join(", ") : form.cast || ""} onChange={(e) => set("cast", e.target.value)} data-testid="field-cast" className="ycn-input" />
            </Field>
            <Field label="Creator / Director">
              <input value={form.creator || ""} onChange={(e) => set("creator", e.target.value)} data-testid="field-creator" className="ycn-input" />
            </Field>
            <Field label="Trailer URL">
              <input value={form.trailer_url || ""} onChange={(e) => set("trailer_url", e.target.value)} data-testid="field-trailer" className="ycn-input" />
            </Field>
            <Field label="Video URL">
              <input value={form.video_url || ""} onChange={(e) => set("video_url", e.target.value)} data-testid="field-video" className="ycn-input" />
            </Field>
          </div>

          {/* Flags */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FlagToggle label="Featured" active={!!form.featured} onClick={() => set("featured", !form.featured)} testId="flag-featured" />
            <FlagToggle label="Trending" active={!!form.is_trending} onClick={() => set("is_trending", !form.is_trending)} testId="flag-trending" />
            <FlagToggle label="YCN Original" active={!!form.is_original} onClick={() => set("is_original", !form.is_original)} testId="flag-original" />
            <FlagToggle label="Published" active={!!form.published} onClick={() => set("published", !form.published)} testId="flag-published" />
          </div>

          <div className="sticky bottom-0 -mx-6 -mb-6 p-4 bg-[#0a0a0c]/95 backdrop-blur border-t border-white/10 flex items-center gap-2 justify-end">
            <button type="button" onClick={onClose} data-testid="drawer-cancel"
              className="px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/10 text-sm">Cancel</button>
            <button type="submit" data-testid="drawer-save"
              className="ycn-btn-primary inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-bold text-white text-sm transition-all">
              <Save size={14} /> {isNew ? "Create title" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1.5">
        {label}{required && <span className="text-red-400"> *</span>}
      </span>
      {children}
    </label>
  );
}

function FlagToggle({ label, active, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      aria-pressed={active}
      className={`px-4 py-3 rounded-2xl border text-sm font-semibold transition ${active ? "bg-gradient-to-r from-violet-600/30 via-blue-600/30 to-orange-500/30 border-white/40 text-white" : "bg-white/5 border-white/10 text-white/60 hover:bg-white/10"}`}>
      {label}
    </button>
  );
}

function ArtUpload({ label, aspect, value, onChange, onFile, uploading, refInput, testId }) {
  const [tab, setTab] = useState("upload"); // upload | url
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mb-1.5">{label}</div>
      <div className={`${aspect} rounded-xl overflow-hidden border border-white/10 bg-white/5 mb-2 relative`}>
        {value
          ? <img src={resolveImgSrc(value)} alt="" className="w-full h-full object-cover" />
          : <div className="w-full h-full bg-gradient-to-br from-violet-700 via-blue-800 to-orange-600" />}
        {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-xs uppercase tracking-widest">Uploading…</div>}
      </div>
      <div className="flex items-center gap-1 mb-2">
        <button type="button" onClick={() => setTab("upload")}
          className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-widest transition ${tab === "upload" ? "bg-white text-black" : "bg-white/5 text-white/60"}`}>Upload</button>
        <button type="button" onClick={() => setTab("url")}
          className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-widest transition ${tab === "url" ? "bg-white text-black" : "bg-white/5 text-white/60"}`}>URL</button>
      </div>
      {tab === "upload" ? (
        <>
          <input ref={refInput} type="file" accept="image/jpeg,image/png,image/webp" hidden
            data-testid={`${testId}-file`}
            onChange={(e) => onFile(e.target.files?.[0])} />
          <button type="button" onClick={() => refInput.current?.click()} disabled={uploading}
            data-testid={`${testId}-upload-btn`}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full ycn-glass hover:bg-white/15 text-sm disabled:opacity-60 transition">
            <Upload size={13} /> Choose file
          </button>
        </>
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
          data-testid={`${testId}-url`}
          placeholder="https://…" className="ycn-input text-xs" />
      )}
    </div>
  );
}
